import { useRef } from 'react'

import { IconSwitch3 } from '@tabler/icons-react'
import { Form } from 'antd'
import Image from 'next/image'
import styled from 'styled-components'

import { Role } from '../adminUsers/roles'

import { SaBuilderSelect } from '@/components/form-components/SaBuilderSelect'
import { Button } from '@/components/shared-components/BUI/Button'
import { Modal } from '@/components/shared-components/BUI/Modal'
import { Text } from '@/components/shared-components/BUI/Text'
import { Flexbox } from '@/components/shared-components/Flexbox'
import { useSupport } from '@/context/supportContext'
import { useSwitchBuilderModal } from '@/context/switchBuilderContext'
import { useUser } from '@/context/userContext'
import { useRoles } from '@/utils/index'

import bpLogo from 'public/buildpass-small.svg'

const CurrentBuilder = styled(Flexbox)`
  background-color: ${({ theme }) => theme.colour.secondary._50};
  padding: 0.5rem;
  margin-top: 0.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  gap: 1rem;
`

const SwitchBuilderModal: React.FC = () => {
  const submitRef = useRef<HTMLButtonElement>(undefined)

  const { switchUsersBuilder, clearOriginalBuilder } = useSupport()
  const { open, closeSwitchBuilderModal } = useSwitchBuilderModal()
  const { builder } = useUser()
  const { hasRole } = useRoles()

  const [form] = Form.useForm()

  const switchBuilder = async (values: Record<string, string>) => {
    await switchUsersBuilder(values.builderId)
  }

  if (!hasRole([Role.SuperAdmin])) return null

  return (
    <Modal
      destroyOnClose // helps with the autoOpenAndFocus prop
      title="Switch Builder - Support Mode"
      open={open}
      onCancel={closeSwitchBuilderModal}
      modalRender={(children) => (
        <Form layout="vertical" form={form} onFinish={switchBuilder}>
          {children}
        </Form>
      )}
      footerLayout="split"
      footer={
        <>
          <Button
            onClick={clearOriginalBuilder}
            variant="secondary-text"
            tabIndex={-1}
          >
            Reset &quot;Home&quot; builder
          </Button>
          <Button
            ref={submitRef}
            type="submit"
            variant="primary"
            icon={<IconSwitch3 size={16} />}
          >
            Switch
          </Button>
        </>
      }
    >
      <Text variant="caption01" secondary="_400" mb="1rem">
        Jump into another builder&apos;s account as your current support user
      </Text>

      <Text>Current</Text>

      <CurrentBuilder alignItems="center" gap="0.5rem">
        <Image
          src={builder.logoUrl ?? bpLogo.src}
          width={40}
          height={24}
          style={{
            objectFit: 'contain',
            objectPosition: 'center',
            ...(!builder?.logoUrl && {
              transform: 'scale(0.6)',
              filter: 'grayscale(1) brightness(0.5)',
            }),
          }}
          alt="builder logo"
        />
        <Flexbox flexDirection="column">
          <Text variant="label02">{builder?.company?.name}</Text>
          <Text variant="caption01" secondary="_500">
            {builder.id}
          </Text>
        </Flexbox>
      </CurrentBuilder>

      <br />

      <Form.Item
        label={<Text>Switch to</Text>}
        name="builderId"
        style={{ marginBottom: 0 }}
        rules={[
          {
            validator: (_, builderId) => {
              return builderId === builder.id
                ? Promise.reject('You are already on this builder')
                : Promise.resolve()
            },
          },
        ]}
      >
        <SaBuilderSelect
          style={{ width: '100%' }}
          autoOpenAndFocus
          onSelect={() => submitRef.current?.focus()}
        />
      </Form.Item>
    </Modal>
  )
}

export default SwitchBuilderModal
